import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePage from '@dashboard/hooks/usePage';
import { fetcher } from '@dashboard/services/request';
import { pageKey } from '@dashboard/lib/queryKeys';
import { Form, FormSubmit, TextInput } from '@dashboard/components/Formik';
import { formatPriceForInput, formatPriceInput } from '@dashboard/services/formatting/price';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import { ImageField } from '@dashboard/components/Formik/components/Image';
import { useTicketGroups } from '../TicketGroupsContext';

export default function TicketTypeEdit({
  ticketType,
  ticketGroupId,
  cancelButton,
  submitButton,
  formSubmitDirection,
}) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();
  const { setHasUnsavedChanges, setIsAddingTicketType, setEditingTicketTypeId } =
    useTicketGroupEdit();
  const { onCancelEditTicketType } = useTicketTypeHelpers();
  const { onDirty } = useTicketTypeHelpers(ticketType?._id);
  const [shouldAddAnother, setShouldAddAnother] = useState(false);

  const { state: ticketGroups, dispatch } = useTicketGroups();
  const ticketGroup = ticketGroups.find(tg => tg._id === ticketGroupId);

  const editTicketTypeMutation = useMutation({
    mutationFn: values =>
      fetcher(
        ticketType?._id
          ? `/sellers/${sellerId}/ticket-types/${ticketType?._id}`
          : `/sellers/${sellerId}/ticket-types`,
        token,
        {
          method: 'POST',
          body: JSON.stringify(values),
        },
      ),
    onSuccess: values => {
      if (ticketType?._id) {
        dispatch({
          type: 'updateTicketType',
          ticketGroupId,
          ticketTypeId: ticketType._id,
          values,
        });
      } else {
        dispatch({ type: 'addTicketType', ticketGroupId, values });
      }

      setIsAddingTicketType(false);

      // delay resetting form to ensure isAddingTicketType is false
      window.setTimeout(() => {
        setHasUnsavedChanges(false);
      }, 250);

      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    },
  });

  return (
    <Form
      onDirty={onDirty}
      initialValues={{
        image: ticketType?.image,
        name: ticketType?.name || '',
        description: ticketType?.description || '',
        capacity: ticketType?.capacity === null ? null : ticketType?.capacity,
        pricing: ticketType?.pricing || 'paid',
        price: ticketType?.price
          ? formatPriceForInput({
              amount: ticketType.price,
              currency: page.eventDetails?.currency,
              style: 'decimal',
            })
          : null,
        discountedFromPrice: ticketType?.discountedFromPrice
          ? formatPriceForInput({
              amount: ticketType.discountedFromPrice,
              currency: page.eventDetails?.currency,
              style: 'decimal',
            })
          : null,
        bookingFee: {
          enabled: ticketType?.bookingFee?.enabled || false,
          type: ticketType?.bookingFee?.type || 'fixed',
          percentageFee: ticketType?.bookingFee?.percentageFee || 0,
          fixedFee: ticketType?.bookingFee?.fixedFee || 0,
        },
        minQuantity: ticketType?.minQuantity || 0,
        maxQuantity: ticketType?.maxQuantity || 10,
        showAvailableQuantity: ticketType?.showAvailableQuantity === false ? false : true,
        hidden: ticketType?.hidden || false,
        hideWhenSoldOut: ticketType?.hideWhenSoldOut || false,
        reference: ticketType?.reference || '',
      }}
      onSubmit={async (values, actions) => {
        try {
          let price = formatPriceInput(values.price, page.eventDetails?.currency);
          let discountedFromPrice = values.discountedFromPrice
            ? formatPriceInput(values.discountedFromPrice, page.eventDetails?.currency)
            : null;

          if (values.pricing === 'free') {
            price = 0;
            discountedFromPrice = null;
          } else if (values.pricing === 'paid') {
            // validate price is greater than 0
            if (price <= 0) {
              return {
                error: 'Price is required for paid ticket types',
              };
            }
          }

          if (!values.minQuantity) {
            values.minQuantity = 0;
          }

          if (!values.maxQuantity) {
            values.maxQuantity = 10;
          }

          const data = await editTicketTypeMutation.mutateAsync({
            ...values,
            price,
            discountedFromPrice,
            pageId: page._id,
            ticketGroupId,
          });

          if (!ticketType?._id) {
            // if new ticket type, reset form
            actions.resetForm();
          } else {
            // if editing, reset form with new values
            actions.resetForm({
              values: {
                image: data.image,
                name: data.name,
                description: data.description,
                capacity: data.capacity,
                pricing: data.pricing,
                price:
                  data.pricing === 'paid'
                    ? formatPriceForInput({
                        amount: data.price,
                        currency: page.eventDetails?.currency,
                        style: 'decimal',
                      })
                    : '',
                discountedFromPrice:
                  data.pricing === 'paid' && data.discountedFromPrice
                    ? formatPriceForInput({
                        amount: data.discountedFromPrice,
                        currency: page.eventDetails?.currency,
                        style: 'decimal',
                      })
                    : '',
                bookingFee: {
                  enabled: data.bookingFee?.enabled,
                  type: data.bookingFee?.type,
                  percentageFee: data.bookingFee?.percentageFee,
                  fixedFee: data.bookingFee?.fixedFee,
                },
                minQuantity: data.minQuantity,
                maxQuantity: data.maxQuantity,
                showAvailableQuantity: data.showAvailableQuantity,
                hidden: data.hidden,
                hideWhenSoldOut: data.hideWhenSoldOut,
                reference: data.reference,
              },
            });
          }

          return {
            data: data,
            onSuccess: () => {
              // if editing ticket type
              if (!ticketType?._id) {
                if (shouldAddAnother) {
                  setShouldAddAnother(false);
                  setEditingTicketTypeId(null);
                  // wait for next tick to set isAddingTicketType
                  // so that the form is reset and auto focused
                  window.setTimeout(() => {
                    setIsAddingTicketType(true);
                  }, 0);
                }
              }
            },
          };
        } catch (error) {
          return {
            error: error,
          };
        }
      }}
      inputClassName="text-sm"
      fields={({ values }) => [
        {
          type: 'custom',
          renderField: () => {
            return (
              <div className="flex gap-x-2 relative">
                <div className="w-[63px] h-[63px]">
                  <ImageField
                    id="image"
                    name="image"
                    image={values.image}
                    previewSize="variant_option"
                    variant="minimal"
                  />
                </div>

                <div className="flex-1">
                  <TextInput
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Name"
                    className="rounded-b-none focus:z-10 !py-[6px] relative text-sm"
                    required
                    border
                    autoFocus={!values.name}
                  />

                  <TextInput
                    id="description"
                    name="description"
                    className="border-t-0 rounded-t-none -mt-px focus:z-10 !py-[6px] relative text-sm"
                    type="text"
                    placeholder="Short description"
                    border
                  />
                </div>
              </div>
            );
          },
          reduceMargin: true,
        },
        {
          type: 'columns',
          columns: 1,
          className: 'ml-[71px]',
          fields: [
            {
              name: 'pricing',
              label: 'Pricing',
              type: 'radioCard',
              options: [
                { label: 'Paid', value: 'paid' },
                { label: 'Free', value: 'free' },
              ],
              columns: 3,
              reduceMargin: true,
            },
            {
              type: 'columns',
              columns: 2,
              fields: [
                {
                  name: 'price',
                  label: 'Price',
                  type: 'number',
                  required: values.pricing === 'paid',
                  reduceMargin: true,
                },
                {
                  name: 'discountedFromPrice',
                  label: 'Discounted from',
                  type: 'number',
                  reduceMargin: true,
                },
              ],
              visible: values.pricing === 'paid',
            },
            {
              type: 'columns',
              columns: 2,
              fields: [
                {
                  name: 'capacity',
                  label: 'Available quantity',
                  placeholder: 'Available quantity',
                  type: 'number',
                  reduceMargin: true,
                },
              ],
            },
            {
              type: 'disclosure',
              label: 'More',
              fields: [
                {
                  type: 'columns',
                  columns: 4,
                  visible: ticketGroup.ticketSelectionType === 'quantity',
                  fields: [
                    {
                      name: 'minQuantity',
                      label: 'Min',
                      tooltip: 'Minimum number of tickets that can be selected',
                      type: 'number',
                      min: 0,
                      max: 999,
                      reduceMargin: true,
                    },
                    {
                      name: 'maxQuantity',
                      label: 'Max',
                      tooltip: 'Maximum number of tickets that can be selected',
                      type: 'number',
                      min: 1,
                      max: 999,
                      reduceMargin: true,
                    },
                  ],
                  reduceMargin: true,
                },
                {
                  type: 'fieldgroup',
                  fields: [
                    {
                      name: 'showAvailableQuantity',
                      label: 'Show available quantity',
                      type: 'checkbox',
                    },
                    {
                      label: 'Hide when sold out',
                      name: 'hideWhenSoldOut',
                      type: 'checkbox',
                      tooltip: 'Automatically hide this ticket type when sold out',
                    },
                    {
                      label: 'Hide ticket type',
                      name: 'hidden',
                      type: 'checkbox',
                      tooltip: 'Hide ticket type and display it with query parameters',
                    },
                  ],
                  reduceMargin: true,
                },
                {
                  name: 'reference',
                  label: 'Reference',
                  type: 'text',
                  tooltip: 'Reference for this ticket type',
                  noMargin: true,
                },
                /*
        {
          name: 'bookingFee.enabled',
          label: 'Booking Fee',
          type: 'checkbox',
          reduceMargin: true,
        },
        {
          name: 'bookingFee.type',
          label: 'Type',
          type: 'select',
          options: [
            { label: 'Fixed', value: 'fixed' },
            { label: 'Percentage', value: 'percentage' },
          ],
          visible: values => values.bookingFee?.enabled,
          inset: true,
        },
        {
          name: 'bookingFee.percentageFee',
          label: 'Percentage Fee',
          type: 'number',
          visible: values =>
            values.bookingFee?.enabled &&
            values.bookingFee?.type === 'percentage',
          inset: true,
        },
        {
          name: 'bookingFee.fixedFee',
          label: 'Fixed Fee',
          type: 'number',
          visible: values =>
            values.bookingFee?.enabled && values.bookingFee?.type === 'fixed',
          inset: true,
        },
        */
              ],
            },
          ],
        },
      ]}
      formSubmit={
        ticketType?._id
          ? null
          : ({ handleSubmit }) => {
              // if new ticket type
              if (!ticketType?._id) {
                return (
                  <FormSubmit>
                    <div className="flex flex-row justify-between w-full items-center">
                      <div>
                        {(ticketGroups.length > 1 || ticketGroup?.ticketTypes?.length > 0) && (
                          <FormSubmit.Cancel appearance="link" onClick={onCancelEditTicketType}>
                            Cancel
                          </FormSubmit.Cancel>
                        )}
                      </div>

                      <div className="flex flex-row gap-x-2">
                        <FormSubmit.Button
                          appearance="secondary"
                          onClick={() => {
                            setShouldAddAnother(true);
                            handleSubmit();
                          }}
                          type="button"
                        >
                          + Add another
                        </FormSubmit.Button>
                        <FormSubmit.Button>Save</FormSubmit.Button>
                      </div>
                    </div>
                  </FormSubmit>
                );
              }
            }
      }
      formSubmitDirection={formSubmitDirection}
      formSubmitAlign="end"
      cancelButton={
        cancelButton
          ? cancelButton
          : {
              label: 'Close',
              onClick: onCancelEditTicketType,
            }
      }
      submitButton={submitButton}
      showState
    />
  );
}
